/**
 * UI State Store for Cedar Heights Music Academy Admin System
 * 
 * This Zustand store manages UI-related state including:
 * - Sidebar open/closed state
 * - Modal management
 * - Theme preferences
 * - Loading states
 * - Navigation state
 * - Mobile/desktop responsive states
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Notification } from '../types';

// UI State interfaces
interface ModalState {
  id: string;
  isOpen: boolean;
  data?: any;
}

interface UIStore {
  // Sidebar state
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  
  // Theme state
  theme: 'light' | 'dark';
  
  // Modal management
  modals: Record<string, ModalState>;
  
  // Notifications (in-app notifications, not toast notifications)
  notifications: Notification[];
  
  // Loading states
  globalLoading: boolean;
  loadingStates: Record<string, boolean>;
  
  // Navigation state
  currentPage: string;
  breadcrumbs: Array<{ label: string; path?: string }>;
  
  // Mobile/responsive state
  isMobile: boolean;
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  
  openModal: (id: string, data?: any) => void;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  isModalOpen: (id: string) => boolean;
  getModalData: (id: string) => any;
  
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: () => void;
  clearAllNotifications: () => void;
  getUnreadNotificationCount: () => number;
  
  setGlobalLoading: (loading: boolean) => void;
  setLoading: (key: string, loading: boolean) => void;
  isLoading: (key: string) => boolean;
  clearAllLoading: () => void;
  
  setCurrentPage: (page: string) => void;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path?: string }>) => void;
  addBreadcrumb: (breadcrumb: { label: string; path?: string }) => void;
  
  setMobile: (isMobile: boolean) => void;
  setScreenSize: (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => void;
}

/**
 * UI state store using Zustand with persistence for user preferences
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      modals: {},
      notifications: [],
      globalLoading: false,
      loadingStates: {},
      currentPage: '',
      breadcrumbs: [],
      isMobile: false,
      screenSize: 'md',

      // Sidebar actions
      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebarCollapsed: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      // Theme actions
      setTheme: (theme: 'light' | 'dark') => {
        set({ theme });
        // Apply theme to document root for CSS variables
        document.documentElement.setAttribute('data-theme', theme);
      },

      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'light' ? 'dark' : 'light';
        get().setTheme(newTheme);
      },

      // Modal actions
      openModal: (id: string, data?: any) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { id, isOpen: true, data },
          },
        }));
      },

      closeModal: (id: string) => {
        set((state) => ({
          modals: {
            ...state.modals,
            [id]: { ...state.modals[id], isOpen: false },
          },
        }));
      },

      closeAllModals: () => {
        set((state) => {
          const closedModals = Object.keys(state.modals).reduce((acc, key) => {
            acc[key] = { ...state.modals[key], isOpen: false };
            return acc;
          }, {} as Record<string, ModalState>);
          return { modals: closedModals };
        });
      },

      isModalOpen: (id: string) => {
        const { modals } = get();
        return modals[id]?.isOpen || false;
      },

      getModalData: (id: string) => {
        const { modals } = get();
        return modals[id]?.data;
      },

      // Notification actions
      addNotification: (notification: Omit<Notification, 'id'>) => {
        const id = `notification-${Date.now()}-${Math.random()}`;
        const newNotification: Notification = {
          ...notification,
          id,
          timestamp: new Date(),
          read: false,
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications],
        }));
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      markNotificationAsRead: (id: string) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }));
      },

      markAllNotificationsAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }));
      },

      clearAllNotifications: () => {
        set({ notifications: [] });
      },

      getUnreadNotificationCount: () => {
        const { notifications } = get();
        return notifications.filter((n) => !n.read).length;
      },

      // Loading actions
      setGlobalLoading: (loading: boolean) => {
        set({ globalLoading: loading });
      },

      setLoading: (key: string, loading: boolean) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: loading,
          },
        }));
      },

      isLoading: (key: string) => {
        const { loadingStates } = get();
        return loadingStates[key] || false;
      },

      clearAllLoading: () => {
        set({ loadingStates: {}, globalLoading: false });
      },

      // Navigation actions
      setCurrentPage: (page: string) => {
        set({ currentPage: page });
      },

      setBreadcrumbs: (breadcrumbs: Array<{ label: string; path?: string }>) => {
        set({ breadcrumbs });
      },

      addBreadcrumb: (breadcrumb: { label: string; path?: string }) => {
        set((state) => ({
          breadcrumbs: [...state.breadcrumbs, breadcrumb],
        }));
      },

      // Responsive actions
      setMobile: (isMobile: boolean) => {
        set({ isMobile });
        // Auto-collapse sidebar on mobile
        if (isMobile) {
          set({ sidebarOpen: false });
        }
      },

      setScreenSize: (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
        set({ screenSize: size });
        // Update mobile state based on screen size
        const isMobile = size === 'xs' || size === 'sm';
        get().setMobile(isMobile);
      },
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user preferences, not temporary UI state
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);

// Selectors for common use cases
export const uiSelectors = {
  sidebarOpen: (state: UIStore) => state.sidebarOpen,
  sidebarCollapsed: (state: UIStore) => state.sidebarCollapsed,
  theme: (state: UIStore) => state.theme,
  globalLoading: (state: UIStore) => state.globalLoading,
  notifications: (state: UIStore) => state.notifications,
  unreadCount: (state: UIStore) => state.getUnreadNotificationCount(),
  currentPage: (state: UIStore) => state.currentPage,
  breadcrumbs: (state: UIStore) => state.breadcrumbs,
  isMobile: (state: UIStore) => state.isMobile,
  screenSize: (state: UIStore) => state.screenSize,
};

// Custom hooks for common UI patterns
export const useSidebar = () => {
  const store = useUIStore();
  return {
    isOpen: store.sidebarOpen,
    isCollapsed: store.sidebarCollapsed,
    toggle: store.toggleSidebar,
    setOpen: store.setSidebarOpen,
    toggleCollapsed: store.toggleSidebarCollapsed,
    setCollapsed: store.setSidebarCollapsed,
  };
};

export const useTheme = () => {
  const store = useUIStore();
  return {
    theme: store.theme,
    setTheme: store.setTheme,
    toggleTheme: store.toggleTheme,
  };
};

export const useModal = (modalId: string) => {
  const store = useUIStore();
  return {
    isOpen: store.isModalOpen(modalId),
    data: store.getModalData(modalId),
    open: (data?: any) => store.openModal(modalId, data),
    close: () => store.closeModal(modalId),
  };
};

export const useNotifications = () => {
  const store = useUIStore();
  return {
    notifications: store.notifications,
    unreadCount: store.getUnreadNotificationCount(),
    add: store.addNotification,
    remove: store.removeNotification,
    markAsRead: store.markNotificationAsRead,
    markAllAsRead: store.markAllNotificationsAsRead,
    clearAll: store.clearAllNotifications,
  };
};

export const useLoading = (key?: string) => {
  const store = useUIStore();
  
  if (key) {
    return {
      isLoading: store.isLoading(key),
      setLoading: (loading: boolean) => store.setLoading(key, loading),
    };
  }
  
  return {
    isLoading: store.globalLoading,
    setLoading: store.setGlobalLoading,
    clearAll: store.clearAllLoading,
  };
};

export default useUIStore;